'use client';

import React, {
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  forwardRef,
} from 'react';
import { motion } from 'framer-motion';
import { ChevronLeft, ChevronRight } from 'lucide-react';

/* ── The row ──────────────────────────────────────────────────────────────────
   One horizontal row of cards, scrolled natively: three to a frame on a
   desktop, two on a tablet, one plus a sliver of the next on a phone. The
   browser owns the gesture, which is the whole reason for doing it this way — a
   mostly-horizontal drag moves the row and a mostly-vertical one still scrolls
   the page, an arbitration a hand-written touch handler only ever approximates.
   `scroll-snap-type` supplies the snap, `scrollTo` supplies the animation, and
   `scrollTo` also cancels whatever scroll was in flight, so there is exactly one
   authoritative position at any moment.

   Everything that moves the row — the two buttons, the arrow keys, a resize, a
   recommendation arriving from elsewhere on the page — goes through goToIndex,
   and the leading card is read back off the scroller rather than tracked in
   parallel. A finger and a button press therefore end in the same state, and
   nothing can go stale. */

/* How far the row curves away at the edges of the frame.

   Rotation, opacity and a small scale — but no push back along Z. Pushing a card
   away shrinks it about its own centre, which opens a margin on all four sides
   and reads as a tile that got smaller rather than a surface that turned. A
   rotation hinged on the card's inner edge keeps that edge flush with its
   neighbour and foreshortens only the outer one, which is what actually looks
   like depth. */
const DEPTH_ROTATE = 24; // degrees at the frame edge
const DEPTH_FADE = 0.16; // opacity given up at the frame edge
const DEPTH_SHRINK = 0.05; // scale given up at the frame edge

/* How much of the middle of the frame is dead flat. Without it the angle is a
   pure function of position, so on a phone — where the card fills 86% of the
   frame and its middle therefore sits a little left of the frame's — the card
   being read carries a permanent tilt and the softer text that comes with it.
   The card in focus should be square-on at every width; only the ones heading
   for an edge should turn.

   0.3 is a third of the half-frame, roughly one card at the desktop's three-up.
   It leaves the desktop flanks at a full angle, keeps the phone's card dead flat
   with its neighbour turned right away, and takes most of the angle off the
   tablet's two-up, where both cards straddle the middle and neither would
   otherwise be square-on. */
const DEPTH_FLAT = 0.3;

export interface CarouselSlide {
  key: string;
  /** Put on the slide, not the card — outside code scrolls to it by id. */
  id?: string;
  /** Reads into the slide's accessible name and the live region. */
  title: string;
  /** Shown in the counter. Defaults to the position. */
  number?: string;
  /** Merged onto the card: background, hover, click, whatever the section needs. */
  cardProps?: React.HTMLAttributes<HTMLDivElement>;
  content: React.ReactNode;
}

export interface CarouselHandle {
  /** Bring a card into the frame if it is not already whole inside it. */
  reveal: (index: number) => void;
  /** The scrolling frame, for a caller that needs to measure where the row sits. */
  element: () => HTMLDivElement | null;
}

interface CarouselProps {
  /** Unique on the page: the viewport's id is built from it. */
  id: string;
  /** Names the carousel for a screen reader. */
  label: string;
  /** What one card is, for the live region: "Service 2 of 6: …". */
  noun: string;
  slides: CarouselSlide[];
  /** Gates the entrance animation; pass the section's own useInView. */
  isInView?: boolean;
  staggerSeconds?: number;
}

const prefersReducedMotion = () =>
  typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/* Where the row rests when a given card is the one being read: that card in the
   middle of the frame. The frame carries half a frame of empty run-up at each
   end (see .m-carousel__viewport::before), so this is reachable for the first
   and last card too, and the clamp is only a guard against rounding.

   This is the single definition of a resting position. The buttons scroll to
   it, and the card the row is currently resting on is found by looking for the
   one whose resting position is nearest — so a swipe, a button and a keypress
   cannot disagree about which card is current. */
const restingScrollFor = (viewport: HTMLDivElement, slide: HTMLDivElement) => {
  const centred = slide.offsetLeft + slide.offsetWidth / 2 - viewport.clientWidth / 2;
  const max = Math.max(0, viewport.scrollWidth - viewport.clientWidth);
  return Math.min(Math.max(centred, 0), max);
};

function CarouselInner(
  { id, label, noun, slides, isInView = true, staggerSeconds = 0.08 }: CarouselProps,
  ref: React.ForwardedRef<CarouselHandle>
) {
  const viewportRef = useRef<HTMLDivElement | null>(null);
  const slideRefs = useRef<(HTMLDivElement | null)[]>([]);
  const cardRefs = useRef<(HTMLDivElement | null)[]>([]);
  const depthOn = useRef(false);
  const [active, setActive] = useState(0);
  const activeRef = useRef(0);
  const [atStart, setAtStart] = useState(true);
  const [atEnd, setAtEnd] = useState(false);
  const scrollFrame = useRef<number | null>(null);
  const count = slides.length;

  /* The row is one surface curving away at the edges of the frame rather than a
     strip of flat tiles. Each card's angle, size and tone come from where it
     sits across the frame — the card in focus faces the reader square-on and
     carries the lift, the ones beside it hinge back on their inner edge — so the
     depth follows the scroll continuously instead of snapping between states.

     Written straight to the DOM. A state update per frame would be a React
     render per card for something the compositor does on its own, and the
     transform goes on an inner element because framer-motion owns the slide's
     own transform for the entrance animation and writing to it would fight
     that. */
  const applyDepth = useCallback(() => {
    const viewport = viewportRef.current;
    if (!viewport) return;
    const half = viewport.clientWidth / 2;
    if (!half) return;

    let nearest = 0;
    let smallest = Number.POSITIVE_INFINITY;
    const positions = slideRefs.current.map((slide) => {
      if (!slide) return null;
      const centre = slide.offsetLeft - viewport.scrollLeft + slide.offsetWidth / 2;
      return Math.max(-1, Math.min(1, (centre - half) / half));
    });
    positions.forEach((p, i) => {
      if (p === null) return;
      if (Math.abs(p) < smallest) {
        smallest = Math.abs(p);
        nearest = i;
      }
    });

    slideRefs.current.forEach((slide, i) => {
      const card = cardRefs.current[i];
      const fromMiddle = positions[i];
      if (!slide || !card || fromMiddle === null) return;

      /* The lift is emphasis, not motion: it says which card is being read. It
         survives a reduced-motion preference, which only takes away the turn. */
      const lifted = i === nearest;
      card.dataset.lift = lifted ? 'true' : 'false';
      slide.style.zIndex = lifted ? '2' : '1';

      if (!depthOn.current) {
        card.style.transform = '';
        card.style.transformOrigin = '';
        card.style.opacity = '';
        return;
      }
      const away = Math.max(0, (Math.abs(fromMiddle) - DEPTH_FLAT) / (1 - DEPTH_FLAT));
      const angle = Math.sign(fromMiddle) * away * DEPTH_ROTATE;
      // Hinged on the edge that faces the middle, so neighbouring cards stay
      // shoulder to shoulder and the gap opens at the frame's edge instead.
      card.style.transformOrigin = fromMiddle < 0 ? 'right center' : 'left center';
      card.style.transform = `rotateY(${angle.toFixed(2)}deg) scale(${(1 - away * DEPTH_SHRINK).toFixed(4)})`;
      card.style.opacity = (1 - away * DEPTH_FADE).toFixed(3);
    });
  }, []);

  /* The turn is motion tied to the scroll, which is exactly what a vestibular
     trigger is, so anyone who asked for reduced motion gets the flat row. */
  useEffect(() => {
    const query = window.matchMedia('(prefers-reduced-motion: reduce)');
    const apply = () => {
      depthOn.current = !query.matches;
      applyDepth();
    };
    apply();
    query.addEventListener('change', apply);
    return () => query.removeEventListener('change', apply);
  }, [applyDepth]);

  /* The current card is the one the row is resting nearest to. Read back from
     scrollLeft rather than tracked separately, so it is true after a swipe, a
     button, a trackpad flick, or the browser scrolling a focused card into
     view. */
  const readState = useCallback(() => {
    const viewport = viewportRef.current;
    if (!viewport) return;
    const max = viewport.scrollWidth - viewport.clientWidth;
    const x = viewport.scrollLeft;
    setAtStart(x <= 2);
    setAtEnd(x >= max - 2);

    let nearest = 0;
    let smallest = Number.POSITIVE_INFINITY;
    slideRefs.current.forEach((el, i) => {
      if (!el) return;
      const distance = Math.abs(restingScrollFor(viewport, el) - x);
      if (distance < smallest) {
        smallest = distance;
        nearest = i;
      }
    });
    activeRef.current = nearest;
    setActive(nearest);
    applyDepth();
  }, [applyDepth]);

  const handleScroll = () => {
    if (scrollFrame.current !== null) return;
    scrollFrame.current = requestAnimationFrame(() => {
      scrollFrame.current = null;
      readState();
    });
  };

  /* The one way the row moves: put the asked-for card in the middle. */
  const goToIndex = useCallback((index: number, instant = false) => {
    const viewport = viewportRef.current;
    const slide = slideRefs.current[index];
    if (!viewport || !slide) return;
    viewport.scrollTo({
      left: restingScrollFor(viewport, slide),
      behavior: instant || prefersReducedMotion() ? 'auto' : 'smooth',
    });
  }, []);

  const goNext = useCallback(
    () => goToIndex(Math.min(activeRef.current + 1, count - 1)),
    [goToIndex, count]
  );
  const goPrev = useCallback(() => goToIndex(Math.max(activeRef.current - 1, 0)), [goToIndex]);

  /* Makes the asked-for card the one being read — in the middle, square-on and
     raised — rather than merely somewhere on screen. A card that is already
     there is left alone, so asking twice does not re-animate anything. */
  const reveal = useCallback(
    (index: number) => {
      const viewport = viewportRef.current;
      const slide = slideRefs.current[index];
      if (!viewport || !slide) return;
      if (Math.abs(restingScrollFor(viewport, slide) - viewport.scrollLeft) <= 2) return;
      goToIndex(index);
    },
    [goToIndex]
  );

  useImperativeHandle(ref, () => ({ reveal, element: () => viewportRef.current }), [reveal]);

  useEffect(() => {
    const viewport = viewportRef.current;
    if (!viewport) return;
    readState();

    /* A width change means the slides are a different size and the leading card
       has to be re-aligned — a breakpoint crossing, a rotation, a resized
       window. A height change (a card opening) must not move anything, which is
       why the width is compared rather than trusted. */
    let width = viewport.clientWidth;
    const observer = new ResizeObserver(() => {
      const current = viewportRef.current;
      if (!current) return;
      if (current.clientWidth === width) {
        // The row did not change size, but a card's height may have; the depth
        // is unaffected and only needs re-asserting if the slides moved.
        return;
      }
      width = current.clientWidth;
      goToIndex(activeRef.current, true);
      applyDepth();
    });
    observer.observe(viewport);
    return () => {
      observer.disconnect();
      if (scrollFrame.current !== null) cancelAnimationFrame(scrollFrame.current);
    };
  }, [readState, goToIndex, applyDepth]);

  // The number of cards can change under us — an article published from the
  // dashboard arrives after the first paint — so the row re-reads itself.
  useEffect(() => {
    slideRefs.current.length = count;
    cardRefs.current.length = count;
    readState();
  }, [count, readState]);

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.defaultPrevented) return;
    if (e.key === 'ArrowRight') {
      e.preventDefault();
      goNext();
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault();
      goPrev();
    }
  };

  const viewportId = `${id}-viewport`;
  const numberOf = (i: number) => slides[i]?.number ?? String(i + 1).padStart(2, '0');

  /* Press and drag with a mouse.

     A touch already moves the row, because the row is a native scroller and the
     browser owns the gesture — that is the whole design, and it is why
     touch-action is left at auto so a mostly-vertical drag still scrolls the
     page. A mouse gets none of that: dragging inside an overflow container does
     nothing at all on a desktop, so the row could only be moved by its two
     buttons or the arrow keys, and every visitor who tried to throw it with the
     pointer found it dead.

     So the mouse is handled explicitly and touch is left entirely alone.
     Anything that is not a mouse returns immediately rather than being
     re-implemented worse than the browser does it.

     Snapping is turned off for the duration. Dragging against `scroll-snap-type:
     x mandatory` fights the drag the whole way — every frame the browser tries
     to pull the row back to the nearest snap point — and restoring it on release
     is what makes the row settle onto a card at the end. */
  const dragFrom = useRef<{ x: number; scroll: number } | null>(null);
  const dragged = useRef(false);
  const [dragging, setDragging] = useState(false);

  const beginDrag = (event: React.PointerEvent<HTMLDivElement>) => {
    if (event.pointerType !== 'mouse' || event.button !== 0) return;
    const el = viewportRef.current;
    if (!el) return;
    dragFrom.current = { x: event.clientX, scroll: el.scrollLeft };
    dragged.current = false;
  };

  const moveDrag = (event: React.PointerEvent<HTMLDivElement>) => {
    const from = dragFrom.current;
    const el = viewportRef.current;
    if (!from || !el) return;
    const dx = event.clientX - from.x;
    /* A few pixels of slack, so a click that wobbles is still a click. */
    if (!dragged.current && Math.abs(dx) < 4) return;
    if (!dragged.current) {
      dragged.current = true;
      setDragging(true);
      el.style.scrollSnapType = 'none';
      el.setPointerCapture(event.pointerId);
    }
    /* Prevents the browser turning the drag into a text or image selection,
       which is what makes a hand-rolled drag feel broken. */
    event.preventDefault();
    el.scrollLeft = from.scroll - dx;
  };

  const endDrag = (event: React.PointerEvent<HTMLDivElement>) => {
    const el = viewportRef.current;
    if (dragFrom.current && dragged.current && el) {
      el.style.scrollSnapType = '';
      /* Nudging by a pixel makes the restored snap take effect now rather than
         at whatever the next scroll happens to be. */
      el.scrollTo({ left: el.scrollLeft, behavior: 'smooth' });
      if (el.hasPointerCapture(event.pointerId)) el.releasePointerCapture(event.pointerId);
    }
    dragFrom.current = null;
    setDragging(false);
  };

  /* A drag that ends on a card must not also open it. The flag is cleared here
     rather than on pointerup, because the click arrives after. */
  const onClickCapture = (event: React.MouseEvent<HTMLDivElement>) => {
    if (!dragged.current) return;
    event.preventDefault();
    event.stopPropagation();
    dragged.current = false;
  };

  return (
    <div
      className="m-carousel"
      role="group"
      aria-roledescription="carousel"
      aria-label={label}
      onKeyDown={onKeyDown}
    >
      <div className="m-carousel__bar">
        <div className="m-carousel__progress">
          <span className="m-carousel__rail" aria-hidden="true">
            <span
              style={{
                width: `${100 / Math.max(count, 1)}%`,
                transform: `translateX(${active * 100}%)`,
              }}
            />
          </span>
          <span className="m-carousel__count font-mono uppercase" aria-hidden="true">
            {numberOf(active)} / {numberOf(count - 1)}
          </span>
        </div>
        <div className="m-carousel__buttons">
          <button
            type="button"
            onClick={goPrev}
            disabled={atStart}
            aria-controls={viewportId}
            aria-label={`Previous ${noun.toLowerCase()}s`}
            className="m-carousel__nav"
          >
            <ChevronLeft aria-hidden="true" style={{ width: 16, height: 16 }} />
          </button>
          <button
            type="button"
            onClick={goNext}
            disabled={atEnd}
            aria-controls={viewportId}
            aria-label={`Next ${noun.toLowerCase()}s`}
            className="m-carousel__nav"
          >
            <ChevronRight aria-hidden="true" style={{ width: 16, height: 16 }} />
          </button>
        </div>
      </div>

      {/* Announces the move for a screen reader without repeating the cards,
          which are all in the document and all reachable by Tab. */}
      <span className="sr-only" aria-live="polite">
        {count > 0 ? `${noun} ${active + 1} of ${count}: ${slides[active]?.title ?? ''}` : ''}
      </span>

      <div
        id={viewportId}
        ref={viewportRef}
        onScroll={handleScroll}
        onPointerDown={beginDrag}
        onPointerMove={moveDrag}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
        onClickCapture={onClickCapture}
        /* Cards that are links — the insights row — otherwise start the
           browser's own link drag on mousedown, which swallows the pointer
           stream and leaves the row dead while the services row, whose cards
           are plain divs, moved perfectly well. One prevented dragstart is the
           whole fix. */
        onDragStart={(event) => event.preventDefault()}
        className="m-carousel__viewport scrollbar-hide"
        style={{ cursor: dragging ? 'grabbing' : undefined }}
      >
        {slides.map((slide, i) => (
          <motion.div
            key={slide.key}
            ref={(el) => {
              slideRefs.current[i] = el;
            }}
            id={slide.id}
            initial={{ opacity: 0, y: 24 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: i * staggerSeconds, duration: 0.5 }}
            className="m-carousel__slide"
            role="group"
            aria-roledescription="slide"
            aria-label={`${i + 1} of ${count}: ${slide.title}`}
          >
            <div
              {...slide.cardProps}
              ref={(el) => {
                cardRefs.current[i] = el;
              }}
              className={`m-carousel__card${
                slide.cardProps?.className ? ` ${slide.cardProps.className}` : ''
              }`}
            >
              {slide.content}
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
}

const Carousel = forwardRef<CarouselHandle, CarouselProps>(CarouselInner);
Carousel.displayName = 'Carousel';
export default Carousel;
